import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardList, ExternalLink, HelpCircle, Loader2, MessageSquareWarning, Search, Star, ThumbsDown, ThumbsUp, UserCheck } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';

const API = 'api/replai/v2/reviews/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const SLACK = 'https://lamptwist.slack.com/archives/C02EZ7RLE1Z/p';

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-sm ${active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
    {children}
  </button>
);

const Stars = ({ n }) => (
  <span className="inline-flex" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className={i <= (n || 0) ? (n >= 4 ? 'fill-green-600 text-green-600' : n === 3 ? 'fill-amber-500 text-amber-500' : 'fill-red-600 text-red-600') : 'text-[var(--line)]'} />)}
  </span>
);

const Confidence = ({ r }) => {
  if (r.status === 'pending') return <span className="rounded-full bg-[var(--line-2)] px-2 py-0.5 text-xs text-[var(--ink-2)]">Looking up…</span>;
  if (r.status === 'backlog') return <span className="rounded-full bg-[var(--line-2)] px-2 py-0.5 text-xs text-[var(--ink-2)]">Not looked up</span>;
  if (!r.customer_email) return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Customer unknown</span>;
  if (r.confidence === 'high') return <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">Matched</span>;
  return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Probably · {r.confidence}</span>;
};

const ReviewCard = ({ r, onChange, team, lead }) => {
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [open, setOpen] = useState(false);
  const post = (body) => {
    setBusy(true);
    return axiosInstance.post(`${API}${r.id}/`, body).then(({ data }) => onChange(data)).finally(() => setBusy(false));
  };
  const analysing = r.analysing && !r.analysis;
  return (
    <li className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Stars n={r.stars} />
        <span className="font-semibold">{r.title || '(no title)'}</span>
        <span className="text-sm text-[var(--ink-3)]">{r.reviewer} · {r.source === 'google' ? 'Google' : 'Trustpilot'} · {timeAgo(r.at)}</span>
        {r.company_replied && <span className="rounded-full bg-[var(--line-2)] px-2 py-0.5 text-xs text-[var(--ink-2)]">We replied publicly</span>}
        <span className="ml-auto flex items-center gap-2">
          <Confidence r={r} />
          {r.checked_at && <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle2 size={13} /> {r.checked_by}</span>}
        </span>
      </div>
      <p className={`mt-2 whitespace-pre-wrap text-[0.95rem] leading-relaxed ${open ? '' : 'line-clamp-3'}`}>{r.text}</p>
      {r.text && r.text.length > 220 && (
        <button type="button" onClick={() => setOpen(!open)} className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">{open ? 'Less' : 'More'}</button>
      )}

      <div className="mt-3 grid gap-3 rounded-md bg-[var(--paper)] p-3 text-sm md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="min-w-0 space-y-1">
          {r.customer_email ? (
            <>
              <div><span className="font-semibold">{r.customer_name || r.reviewer}</span> <span className="text-[var(--ink-3)]">{r.customer_email}</span></div>
              {r.match_reason && <div className="text-[var(--ink-3)]">{r.match_reason}{r.candidates > 1 ? ` (${r.candidates} customers have that name)` : ''}</div>}
              {r.orders?.length > 0 && <div>Orders: {r.orders.join(', ')}</div>}
              {r.ticket_id ? (
                <div className="flex flex-wrap items-center gap-x-2">
                  <a href={`${GORGIAS}${r.ticket_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium hover:underline">
                    Ticket {r.ticket_id} <ExternalLink size={12} />
                  </a>
                  <span className="text-[var(--ink-3)]">{r.ticket_status} · {r.ticket_subject}</span>
                </div>
              ) : <div className="text-[var(--ink-3)]">No support ticket from this customer.</div>}
              {r.customer_email && (
                <div className="flex flex-wrap items-center gap-x-2">
                  <span className={r.needs_agent ? 'font-semibold text-amber-800' : 'text-[var(--ink-3)]'}>Counts for</span>
                  {!lead ? <span className="font-medium">{r.agent || 'nobody'}</span> : <select value={r.agent_override || ''} disabled={busy} onChange={(e) => post({ agent: e.target.value })}
                    className="rounded-md border border-[var(--line)] bg-[var(--surface)] px-1.5 py-0.5 font-medium">
                    <option value="">{r.agent_auto ? `${r.agent_auto} (who replied)` : 'nobody'}</option>
                    {team.filter((a) => a !== r.agent_auto).map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>}
                  {r.agent_override && <span className="text-xs text-[var(--ink-3)]">changed by {r.agent_override_by}</span>}
                </div>
              )}
              {r.unanswered > 0 && (
                <div className="flex items-center gap-1.5 font-semibold text-red-700"><MessageSquareWarning size={14} /> {r.unanswered} customer message(s) never answered</div>
              )}
            </>
          ) : r.status === 'backlog' ? (
            <div className="flex flex-wrap items-center gap-2 text-[var(--ink-2)]">An older review, not looked up yet.
              <button type="button" disabled={busy} onClick={() => post({ lookup: true }).then(() => onChange({ ...r, status: 'pending' }))}
                className="rounded-md border border-[var(--line)] px-2 py-1 text-xs hover:bg-[var(--surface)]">Find the customer</button></div>
          ) : (
            <div className="text-[var(--ink-2)]">{r.status === 'pending' ? 'Replai is looking up this customer.' : (r.match_reason || 'No customer found under that name.')}</div>
          )}
          {r.summary && <div className="whitespace-pre-wrap pt-1 text-[var(--ink-2)]">{r.summary}</div>}
        </div>
        <div className="flex flex-row flex-wrap items-start gap-2 md:flex-col">
          {r.slack_ts && <a href={`${SLACK}${r.slack_ts.replace('.', '')}`} target="_blank" rel="noreferrer" className="text-xs text-[var(--ink-2)] hover:underline">Slack thread</a>}
        </div>
      </div>

      {(r.analysis || analysing) && (
        <div className="mt-3 rounded-md border border-[var(--line)] p-3 text-sm">
          <div className="mb-1 flex items-center gap-1.5 font-semibold"><ClipboardList size={14} /> How this case was handled</div>
          {analysing ? <div className="flex items-center gap-2 text-[var(--ink-3)]"><Loader2 size={14} className="animate-spin" /> Replai is reading the ticket… (about 30 seconds)</div>
            : <div className="whitespace-pre-wrap leading-relaxed text-[var(--ink-2)]">{r.analysis}</div>}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        {r.customer_email && !r.analysis && !analysing && (
          <button type="button" disabled={busy} onClick={() => post({ analyse: true }).then(() => onChange({ ...r, analysing: true }))}
            className="rounded-md border border-[var(--line)] px-3 py-1.5 hover:bg-[var(--paper)]">Analyse: was it the agent?</button>
        )}
        {lead && (r.stars || 0) <= 3 && r.customer_email && (
          <span className="inline-flex overflow-hidden rounded-md border border-[var(--line)]" role="group" aria-label="Was it the agent's fault?">
            {[['agent', "Agent's fault"], ['not_agent', 'Not the agent']].map(([k, label]) => (
              <button key={k} type="button" disabled={busy} onClick={() => post({ fault: r.fault === k ? '' : k })}
                className={`px-3 py-1.5 ${r.fault === k ? (k === 'agent' ? 'bg-red-700 text-white' : 'bg-green-700 text-white') : 'hover:bg-[var(--paper)]'}`}>{label}</button>
            ))}
          </span>
        )}
        {r.fault && <span className="text-xs text-[var(--ink-3)]">set by {r.fault_by}</span>}
        {!r.checked_at
          ? <button type="button" disabled={busy} onClick={() => post({ checked: true })} className="inline-flex items-center gap-1.5 rounded-md bg-[var(--ink)] px-3 py-1.5 font-medium text-white"><UserCheck size={14} /> Mark as checked</button>
          : <button type="button" disabled={busy} onClick={() => post({ checked: false })} className="rounded-md border border-[var(--line)] px-3 py-1.5 hover:bg-[var(--paper)]">Uncheck</button>}
        {r.needs_check && (
          <span className="flex items-center gap-1.5">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="It's this customer: email"
              className="w-56 rounded-md border border-[var(--line)] px-2 py-1.5" />
            <button type="button" disabled={busy || !email.includes('@')} onClick={() => post({ customer_email: email }).then(() => onChange({ ...r, customer_email: email, status: 'pending', analysing: true }))}
              className="rounded-md border border-[var(--line)] px-3 py-1.5 hover:bg-[var(--paper)] disabled:opacity-50">Link</button>
          </span>
        )}
      </div>
    </li>
  );
};

const ReviewsV2 = () => {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState('negative');
  const [unchecked, setUnchecked] = useState(true);
  const [agent, setAgent] = useState('');
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);

  const [lead, setLead] = useState(false);
  useEffect(() => { axiosInstance.get('api/replai/v2/access/').then(({ data }) => setLead(!!data.insights)).catch(() => {}); }, []);
  const load = useCallback(() => axiosInstance.get(API, { params: { days } }).then(({ data }) => setD(data)).catch(() => setD(false)), [days]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const replace = (row) => setD((x) => ({ ...x, reviews: x.reviews.map((r) => (r.id === row.id ? { ...r, ...row } : r)) }));
  const all = (d && d.reviews) || [];
  const agents = useMemo(() => [...new Set(all.map((r) => r.agent).filter(Boolean))].sort(), [all]);
  const toAttribute = (r) => r.needs_check || r.needs_agent;
  const inTab = (r) => (!unchecked || !r.checked_at)
    && (tab === 'positive' ? (r.stars || 0) >= 4 : tab === 'negative' ? (r.stars || 0) <= 3 : toAttribute(r));
  const needle = q.trim().toLowerCase();
  const rows = all.filter((r) => inTab(r) && (!agent || r.agent === agent)
    && (!needle || [r.reviewer, r.customer_name, r.customer_email, r.title, r.text, String(r.ticket_id || '')].join(' ').toLowerCase().includes(needle)));
  const count = (f) => all.filter(f).length;
  const perAgent = useMemo(() => agents.map((a) => ({
    a, pos: all.filter((r) => r.agent === a && (r.stars || 0) >= 4).length,
    neg: all.filter((r) => r.agent === a && (r.stars || 0) <= 3 && r.fault !== 'not_agent').length,
    notTheirs: all.filter((r) => r.agent === a && (r.stars || 0) <= 3 && r.fault === 'not_agent').length,
  })), [agents, all]);

  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Reviews</h1>
            <p className="prose-body mt-2 text-[var(--ink-2)]">Trustpilot and Google reviews, linked to the customer, ticket and agent. Replai looks each one up as it comes in.</p>
          </div>
          <div className="flex gap-1">{[7, 30, 90, 365].map((n) => <Chip key={n} active={days === n} onClick={() => setDays(n)}>{n === 365 ? '12 months' : `${n} days`}</Chip>)}</div>
        </div>

        {d === false && <p className="mt-8 text-[var(--ink-2)]">Reviews could not be loaded. Reload the page to try again.</p>}
        {d == null && <div className="mt-8 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {d && (
          <>
            {perAgent.length > 0 && (
              <section className="mt-6 flex flex-wrap gap-2">
                {perAgent.map(({ a, pos, neg, notTheirs }) => (
                  <button key={a} type="button" onClick={() => setAgent(agent === a ? '' : a)}
                    className={`rounded-lg border px-3 py-2 text-left text-sm ${agent === a ? 'border-[var(--ink)] bg-[var(--surface)]' : 'border-[var(--line)] bg-[var(--surface)] hover:border-[var(--ink-3)]'}`}>
                    <div className="font-semibold">{a} <span className="font-normal text-[var(--ink-3)]">· {pos + neg + notTheirs} reviews</span></div>
                    <div className="text-[var(--ink-2)]"><span className="text-green-700">{pos} positive</span> · <span className="text-red-700">{neg} negative</span>
                      {notTheirs > 0 && <span className="text-[var(--ink-3)]"> · {notTheirs} not their fault</span>}</div>
                  </button>
                ))}
              </section>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-[var(--line)] pb-3">
              <Chip active={tab === 'negative'} onClick={() => setTab('negative')}><ThumbsDown size={14} className="mr-1 inline" />Negative ({count((r) => (r.stars || 0) <= 3 && (!unchecked || !r.checked_at))})</Chip>
              <Chip active={tab === 'positive'} onClick={() => setTab('positive')}><ThumbsUp size={14} className="mr-1 inline" />Positive ({count((r) => (r.stars || 0) >= 4 && (!unchecked || !r.checked_at))})</Chip>
              <Chip active={tab === 'check'} onClick={() => setTab('check')}><HelpCircle size={14} className="mr-1 inline" />To attribute ({count(toAttribute)})</Chip>
              <label className="ml-2 flex items-center gap-1.5 text-sm text-[var(--ink-2)]">
                <input type="checkbox" checked={unchecked} onChange={(e) => setUnchecked(e.target.checked)} /> Unchecked only
                <span className="text-[var(--ink-3)]">({count((r) => r.checked_at)} checked hidden)</span>
              </label>
              {agent && <span className="ml-2 text-sm text-[var(--ink-2)]">Agent: <b>{agent}</b> <button type="button" className="underline" onClick={() => setAgent('')}>clear</button></span>}
              <label className="ml-auto flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5">
                <Search size={15} className="text-[var(--ink-3)]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reviewer, customer, ticket" className="w-60 bg-transparent text-sm outline-none" />
              </label>
            </div>
            {tab === 'check' && <p className="mt-3 text-sm text-[var(--ink-2)]">Reviews still to attribute: Replai isn't sure who the customer is (link them by email), or the customer is known but no agent is (pick one under "Counts for"). Mark it as checked when done.</p>}
            {rows.length ? <ul className="mt-5 space-y-3">{rows.map((r) => <ReviewCard key={r.id} r={r} onChange={replace} team={(d && d.team) || []} lead={lead} />)}</ul>
              : <p className="mt-6 text-[var(--ink-2)]">Nothing here.</p>}
          </>
        )}
      </div>
    </V2Shell>
  );
};

export default ReviewsV2;
